import React from "react";
import { alpha } from "@mui/material/styles";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { Tab, Visibility, VisibilityOff } from "@mui/icons-material";
import { msSinceIso, formatTeamPresenceAge } from "../utils/teamPresenceFormatting";
import ClientEnvironmentIcons from "./ClientEnvironmentIcons";

/** Diferença em ms abaixo da qual tratamos “tempo na tela” e “última interação” como iguais (evita texto duplicado). */
const SCREEN_IDLE_MERGE_MS = 2500;

/**
 * Chips de abas (legado / Xpro 4) e lista de telas por aba no Xpro 4.
 */
export default function TeamPresenceTabBreakdown({
  show,
  socketTabs,
  presenceDetail,
  tLive,
  screenTitleForPath,
  livePresenceNowMs,
  density = "default",
  /** Quando false, ícones de device/browser/versão ficam só no cabeçalho do card. */
  showTabEnvIcons = true,
  /** Uma única aba Xpro 4: layout compacto sem chips redundantes. */
  compactSingleTab = false,
  /** Exibe o path cru abaixo do título amigável. Desligar em telas de presença “humana”. */
  showPath = true,
}) {
  const det = presenceDetail;
  const chipH = density === "dense" ? 26 : 28;
  const tabIcon = density === "dense" ? 12 : 14;

  if (!show || socketTabs <= 0) return null;

  const x4Tabs = det?.xpro4Tabs ?? [];
  const hasX4Detail = x4Tabs.length > 0;
  const listMaxHeight = density === "dense" ? 140 : 160;
  const showTabCountChips = !compactSingleTab || det?.legacyTabCount > 0;

  return (
    <Stack spacing={density === "dense" ? 0.75 : 1} sx={{ minWidth: 0 }}>
      {showTabCountChips ? (
        <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 0.75, rowGap: 0.5, alignItems: "center" }}>
          {det && (det.legacyTabCount > 0 || x4Tabs.length > 0) ? (
            <>
              {det.legacyTabCount > 0 ? (
                <Tooltip title={tLive("dashboardPage.live.teamTabsLegacyTooltip")}>
                  <Chip
                    size="small"
                    icon={<Tab sx={{ "&&": { fontSize: tabIcon } }} />}
                    label={tLive("dashboardPage.live.teamTabsLegacyBadge", { count: det.legacyTabCount })}
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      maxWidth: "100%",
                      height: chipH,
                      bgcolor: (th) => alpha(th.palette.warning.dark, th.palette.mode === "dark" ? 0.4 : 0.18),
                      color: "text.primary",
                      "& .MuiChip-icon": { color: "inherit" },
                      "& .MuiChip-label": { py: 0 },
                    }}
                  />
                </Tooltip>
              ) : null}
              {x4Tabs.length > 1 ? (
                <Tooltip title={tLive("dashboardPage.live.teamTabsXpro4Tooltip")}>
                  <Chip
                    size="small"
                    icon={<Tab sx={{ "&&": { fontSize: tabIcon } }} />}
                    label={tLive("dashboardPage.live.teamTabsXpro4Badge", { count: x4Tabs.length })}
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      maxWidth: "100%",
                      height: chipH,
                      bgcolor: (th) => alpha(th.palette.primary.dark, th.palette.mode === "dark" ? 0.45 : 0.14),
                      color: "text.primary",
                      "& .MuiChip-icon": { color: "inherit" },
                      "& .MuiChip-label": { py: 0 },
                    }}
                  />
                </Tooltip>
              ) : null}
            </>
          ) : (
            <Chip
              size="small"
              icon={<Tab sx={{ "&&": { fontSize: tabIcon } }} />}
              label={tLive("dashboardPage.live.openTabsCount", { count: socketTabs })}
              variant="filled"
              sx={{
                fontWeight: 600,
                maxWidth: "100%",
                height: chipH,
                bgcolor: (th) => alpha(th.palette.success.dark, th.palette.mode === "dark" ? 0.35 : 0.12),
                color: "text.primary",
                "& .MuiChip-icon": { color: "inherit" },
                "& .MuiChip-label": { py: 0 },
              }}
            />
          )}
        </Stack>
      ) : null}

      {hasX4Detail ? (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          {!compactSingleTab ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.375, fontWeight: 600, lineHeight: 1.35, letterSpacing: 0.02 }}
            >
              {tLive("dashboardPage.live.teamXpro4TabsHeading")}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, fontWeight: 600, lineHeight: 1.35, letterSpacing: 0.04, textTransform: "uppercase", fontSize: "0.65rem" }}
            >
              {tLive("dashboardPage.live.teamPresenceCurrentScreen")}
            </Typography>
          )}
          <Box
            sx={{
              maxHeight: x4Tabs.length > 2 ? listMaxHeight : "none",
              overflowY: x4Tabs.length > 2 ? "auto" : "visible",
              pr: x4Tabs.length > 2 ? 0.25 : 0,
              scrollbarWidth: "thin",
            }}
          >
            <Stack spacing={density === "dense" ? 0.5 : 0.625}>
              {x4Tabs.map((tab) => {
                const title = screenTitleForPath(tab.path);
                const onScreenMs = msSinceIso(tab.since, livePresenceNowMs);
                const idleMs = msSinceIso(tab.lastActivityAt, livePresenceNowMs);
                const durScreen = formatTeamPresenceAge(onScreenMs, tLive);
                const durIdle = formatTeamPresenceAge(idleMs, tLive);
                const screenOk = onScreenMs != null && Number.isFinite(onScreenMs);
                const idleOk = idleMs != null && Number.isFinite(idleMs);
                const mergedTimes =
                  screenOk && idleOk && Math.abs(onScreenMs - idleMs) <= SCREEN_IDLE_MERGE_MS;

                let timeLine;
                if (mergedTimes) {
                  timeLine = compactSingleTab
                    ? tLive("dashboardPage.live.teamPresenceOnScreen", { duration: durScreen })
                    : tLive("dashboardPage.live.teamPresenceScreenIdleMerged", { duration: durScreen });
                } else if (screenOk && idleOk) {
                  timeLine = tLive("dashboardPage.live.teamPresenceScreenIdleDual", {
                    durationScreen: durScreen,
                    durationIdle: durIdle,
                  });
                } else if (screenOk) {
                  timeLine = tLive("dashboardPage.live.teamPresenceOnScreen", { duration: durScreen });
                } else if (idleOk) {
                  timeLine = tLive("dashboardPage.live.teamPresenceIdle", { duration: durIdle });
                } else {
                  timeLine = "—";
                }

                const focusTip = tab.focused
                  ? tLive("dashboardPage.live.teamPresenceFocusYes")
                  : tLive("dashboardPage.live.teamPresenceFocusNo");

                const showEnvOnTab = showTabEnvIcons && (tab.deviceType || tab.browserName || tab.appVersion || tab.appTheme);

                return (
                  <Box
                    key={tab.tabKey}
                    sx={{
                      borderRadius: 1.25,
                      px: 1.1,
                      py: compactSingleTab ? 0.75 : density === "dense" ? 0.5 : 0.625,
                      minWidth: 0,
                      bgcolor: (th) => alpha(th.palette.text.primary, th.palette.mode === "dark" ? 0.06 : 0.035),
                      border: 1,
                      borderColor: (th) => alpha(th.palette.divider, compactSingleTab ? 0.55 : 0.85),
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ minWidth: 0 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: compactSingleTab ? "0.8125rem" : density === "dense" ? "0.75rem" : "0.8125rem",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: compactSingleTab ? 3 : 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                          title={tab.path || title || undefined}
                        >
                          {title}
                        </Typography>
                        {showPath && title !== tab.path && tab.path ? (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{
                              display: "block",
                              mt: 0.25,
                              lineHeight: 1.35,
                              fontSize: "0.65rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={tab.path}
                          >
                            {tab.path}
                          </Typography>
                        ) : null}
                      </Box>
                      <Tooltip title={focusTip}>
                        <Box
                          component="span"
                          aria-label={focusTip}
                          sx={{
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: compactSingleTab ? 30 : 28,
                            height: compactSingleTab ? 30 : 28,
                            borderRadius: 1,
                            color: tab.focused ? "success.main" : "text.secondary",
                            bgcolor: (th) =>
                              alpha(
                                tab.focused ? th.palette.success.main : th.palette.action.active,
                                tab.focused ? (th.palette.mode === "dark" ? 0.22 : 0.12) : 0.08
                              ),
                          }}
                        >
                          {tab.focused ? (
                            <Visibility sx={{ fontSize: compactSingleTab ? 17 : density === "dense" ? 16 : 17 }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: compactSingleTab ? 17 : density === "dense" ? 16 : 17, opacity: 0.85 }} />
                          )}
                        </Box>
                      </Tooltip>
                    </Stack>

                    {showEnvOnTab ? (
                      <Box sx={{ mt: 0.625 }}>
                        <ClientEnvironmentIcons
                          deviceType={tab.deviceType}
                          browserName={tab.browserName}
                          appVersion={tab.appVersion}
                          appTheme={tab.appTheme}
                          size="small"
                        />
                      </Box>
                    ) : null}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: showEnvOnTab ? 0.5 : 0.375,
                        lineHeight: 1.45,
                        fontSize: density === "dense" ? "0.68rem" : "0.7rem",
                      }}
                    >
                      {timeLine}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}
